import { CarrierName, Dtos } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminAgencyPreviewsQueryKey } from "../agency.keys";

export const useAdminCreateAgency = () => {
	const queryClient = useQueryClient();
	const { post, put } = useRequest();

	const mutation = useMutation({
		mutationFn: async ({
			agency,
		}: {
			agency: Dtos.Admin.Agency.Create.Request;
			carrierAgencies: (Dtos.Admin.CarrierAgencyRelationships.Create.Request & {
				carrierName: CarrierName;
			})[];
			onSuccess: (agencyId: string) => void;
			onError: (errors: Error[]) => void;
		}) => {
			return await post<{ props: Dtos.Admin.Agency.Create.Response }>(
				`/v2/admin/agencies`,
				agency,
				"json",
			);
		},
		onSuccess: async (data, { carrierAgencies, onSuccess, onError }) => {
			queryClient.invalidateQueries({ queryKey: adminAgencyPreviewsQueryKey() });
			const agencyId = data.props.id;

			const upsertRequests = carrierAgencies.map(async ({ carrierName, ...agencyData }) => {
				// Upsert agency carrier relationship
				return await put(
					`/v2/admin/agencies/${agencyId}/carriers/${carrierName}`,
					agencyData,
					"none",
				);
			});

			const results = await Promise.allSettled(upsertRequests);
			const errors = results
				.filter((result) => result.status === "rejected")
				.map((result: PromiseRejectedResult) => new Error(result.reason));

			if (errors.length > 0) {
				onError(errors);
			} else {
				onSuccess(agencyId);
			}
		},
	});

	return {
		createAgency: mutation.mutate,
		createAgencyLoading: mutation.isPending,
		createAgencyError: mutation.error,
		dismissCreateAgencyError: () => {
			mutation.reset();
		},
	};
};
